// src/pages/admin/RenewalManager.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  getDoc,
  updateDoc,
  doc,
  query,
  orderBy,
  where,
  arrayRemove
} from 'firebase/firestore';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  X,
  RefreshCw,
  Plus,
  UserX,
  Pause,
  Mail,
  Clock,
  Filter,
  Users,
  TrendingDown,
  AlertCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

// ─── Constants ─────────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
const DAY_LABELS_FULL = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Filter tabs: value = sessionsRemaining, -1 means "all"
const FILTER_TABS = [
  { value: 0,  label: '0 left',  description: 'Completed — need renewal or deactivation', urgent: true },
  { value: 1,  label: '1 left',  description: 'Last session coming up' },
  { value: 2,  label: '2 left',  description: '2 sessions remaining' },
  { value: 3,  label: '3 left',  description: '3 sessions remaining' },
  { value: -1, label: 'All',     description: 'All enrolled students' },
];

// Status filter chips for the "Not Enrolled" tab
const UNENROLLED_STATUS_FILTERS = [
  { value: 'active',    label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending',   label: 'Pending' },
  { value: 'expired',   label: 'Expired' },
  { value: 'all',       label: 'All' },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${m} ${ampm}`;
}

function dayLabel(value) {
  const idx = DAYS_OF_WEEK.indexOf(value);
  return idx >= 0 ? DAY_LABELS_FULL[idx] : value;
}

function sessionsRemaining(student) {
  const completed = student.sessionsCompleted ?? 0;
  const total     = student.sessionsTotal     ?? 6;
  return Math.max(0, total - completed);
}

// Append-only statusHistory entry (mirrors backend appendStatusHistory)
function appendStatusHistoryFE(child, status, source) {
  const existing = Array.isArray(child && child.statusHistory) ? child.statusHistory : [];
  return [...existing, { status, changedAt: new Date().toISOString(), source }];
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function RemainingBadge({ remaining }) {
  if (remaining === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
        <AlertCircle size={11} /> Completed
      </span>
    );
  }
  if (remaining === 1) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
        <Clock size={11} /> 1 left
      </span>
    );
  }
  if (remaining === 2) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">
        <Clock size={11} /> 2 left
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
      <Clock size={11} /> {remaining} left
    </span>
  );
}

function CourseStatusBadge({ status }) {
  const map = {
    active:    'bg-green-100 text-green-700',
    completed: 'bg-blue-100 text-blue-700',
    expired:   'bg-gray-100 text-gray-500',
    pending:   'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-500'}`}>
      {status ?? '—'}
    </span>
  );
}

// ─── Student Row ───────────────────────────────────────────────────────────────

function StudentRow({ entry, onRenew, onDeactivate, onPause, actionLoading, db }) {
  const { student, cls, teacher, userCourseStatus } = entry;
  const remaining = sessionsRemaining(student);
  const isLoading = actionLoading === `${cls.id}__${student.childId}`;
  const [adjustAmount, setAdjustAmount] = useState(6);

  // Session history state
  const [expanded, setExpanded] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsFetched, setSessionsFetched] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);

  const fetchSessions = async () => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const q = query(
        collection(db, 'sessions'),
        where('classId', '==', cls.id),
        orderBy('scheduledAt', 'asc')
      );
      const snap = await getDocs(q);
      const allSessions = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Key format confirmed from AttendanceManager: just childId.
      // Also check userId_childId as a fallback for legacy data.
      const filtered = allSessions.filter(s => {
        const att = s.attendance || {};
        return (
          Object.prototype.hasOwnProperty.call(att, student.childId) ||
          Object.prototype.hasOwnProperty.call(att, `${student.userId}_${student.childId}`) ||
          Object.keys(att).some(k => k.startsWith(student.childId + '_') || k === student.childId)
        );
      });

      setSessions(filtered);
      setSessionsFetched(true);
    } catch (err) {
      console.error('fetchSessions error:', err);
      setSessionsError(
        'Could not load sessions. A Firestore index may be required — check the browser console for a link to create it.'
      );
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !sessionsFetched) {
      fetchSessions();
    }
  };

  const formatSessionDate = (scheduledAt) => {
    if (!scheduledAt) return '—';
    try {
      return new Date(scheduledAt).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      });
    } catch {
      return String(scheduledAt);
    }
  };

  const getAttStatus = (session) => {
    const att = session.attendance || {};
    return att[student.childId] ?? att[`${student.userId}_${student.childId}`] ?? null;
  };

  const StatusDot = ({ status }) => {
    const colours = {
      present: 'bg-green-500',
      absent: 'bg-red-500',
      rescheduled: 'bg-yellow-400',
    };
    return (
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colours[status] ?? 'bg-gray-300'}`} />
    );
  };

  const statusLabel = (status) => {
    const labels = { present: 'Present', absent: 'Absent', rescheduled: 'Rescheduled' };
    return labels[status] ?? 'Not marked';
  };

  const statusTextColour = (status) => {
    if (status === 'present') return 'text-green-600';
    if (status === 'absent') return 'text-red-500';
    if (status === 'rescheduled') return 'text-yellow-600';
    return 'text-gray-400';
  };

  return (
    <div className={`bg-white border rounded-xl p-4 flex items-center gap-4 flex-wrap transition-colors ${
      remaining === 0 ? 'border-red-200' :
      remaining === 1 ? 'border-orange-200' :
      remaining === 2 ? 'border-yellow-200' :
      'border-gray-200'
    }`}>

      {/* Student + parent info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="font-semibold text-gray-800">{student.childName}</p>
          <RemainingBadge remaining={remaining} />
          <CourseStatusBadge status={userCourseStatus} />
          {student.enrollmentType === 'trial' && (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              Trial
            </span>
          )}
        </div>

        {/* Parent contact — prominent for quick outreach */}
        <p className="text-sm text-gray-700">
          {student.parentFirstName} {student.parentLastName}
          <span className="text-gray-400 mx-1.5">·</span>
          <a
            href={`mailto:${student.parentEmail}`}
            className="text-purple-600 hover:underline"
          >
            {student.parentEmail}
          </a>
        </p>

        {/* Class slot */}
        <p className="text-xs text-gray-400 mt-0.5">
          {dayLabel(cls.dayOfWeek)} at {formatTime(cls.time)}
          <span className="mx-1.5">·</span>
          {teacher?.name ?? cls.teacherName}
          <span className="mx-1.5">·</span>
          {cls.type} · {cls.dialect}
        </p>

        {/* Session progress bar */}
        <div className="flex items-center gap-2 mt-2">
          <div className="w-32 bg-gray-100 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${
                remaining === 0 ? 'bg-red-500' :
                remaining === 1 ? 'bg-orange-500' :
                remaining === 2 ? 'bg-yellow-500' :
                'bg-purple-500'
              }`}
              style={{
                width: `${Math.min(100, ((student.sessionsCompleted ?? 0) / (student.sessionsTotal ?? 6)) * 100)}%`
              }}
            />
          </div>
          <span className="text-xs text-gray-500">
            {student.sessionsCompleted ?? 0} / {student.sessionsTotal ?? 6} sessions
          </span>
        </div>

        {/* Session history toggle */}
        <button
          onClick={handleToggle}
          className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 mt-1 cursor-pointer"
        >
          {expanded
            ? <><ChevronDown size={12} />▾ Session history</>
            : <><ChevronRight size={12} />▸ Session history</>
          }
        </button>

        {/* Expandable session list */}
        {expanded && (
          <div className="mt-2 space-y-1 border-t border-gray-100 pt-2">
            {sessionsLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-500 flex-shrink-0" />
                Loading sessions…
              </div>
            ) : sessionsError ? (
              <p className="text-xs text-red-500">{sessionsError}</p>
            ) : sessions.length === 0 ? (
              <p className="text-xs text-gray-400 mt-2 pl-1">No recorded sessions found.</p>
            ) : (
              sessions.map(s => {
                const status = getAttStatus(s);
                return (
                  <div key={s.id} className="flex items-center gap-2 text-xs text-gray-600 py-0.5">
                    <StatusDot status={status} />
                    <span>{formatSessionDate(s.scheduledAt)}</span>
                    <span className={statusTextColour(status)}>{statusLabel(status)}</span>
                    {s.isMakeup && (
                      <span className="text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                        ↻ makeup
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:flex-shrink-0">
        {/* Mailto shortcut */}
        <a
          href={`mailto:${student.parentEmail}?subject=Arabic Course Renewal — ${student.childName}`}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
          title="Email parent"
        >
          <Mail size={13} />
          Email
        </a>

        {/* Adjust sessions — input defaults to 6, accepts negatives */}
        <div className="flex items-center rounded-lg border overflow-hidden"
          style={{ borderColor: adjustAmount < 0 ? '#fed7aa' : '#d8b4fe' }}
        >
          <input
            type="number"
            value={adjustAmount}
            onChange={e => setAdjustAmount(parseInt(e.target.value, 10) || 0)}
            onFocus={e => e.target.select()}
            className="w-14 px-2 py-2 text-xs text-center text-gray-700 focus:outline-none focus:bg-gray-50"
            style={{ borderRight: adjustAmount < 0 ? '1px solid #fed7aa' : '1px solid #d8b4fe' }}
            title="Sessions to adjust (negative to remove)"
          />
          <button
            onClick={() => onRenew(cls, student, adjustAmount)}
            disabled={isLoading || adjustAmount === 0}
            className={`flex items-center gap-1.5 px-3 py-2 text-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              adjustAmount < 0
                ? 'bg-orange-400 hover:bg-orange-500'
                : 'bg-purple-600 hover:bg-purple-700'
            }`}
            title={adjustAmount >= 0 ? `Add ${adjustAmount} sessions` : `Remove ${Math.abs(adjustAmount)} sessions`}
          >
            {isLoading
              ? <RefreshCw size={13} className="animate-spin" />
              : <Plus size={13} />
            }
            {adjustAmount >= 0 ? `+${adjustAmount}` : adjustAmount} Sessions
          </button>
        </div>

        {/* Pause */}
        <button
          onClick={() => onPause(cls, student)}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-amber-200 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Pause — track for return"
        >
          {isLoading
            ? <RefreshCw size={13} className="animate-spin" />
            : <Pause size={13} />
          }
          Pause
        </button>

        {/* Deactivate */}
        <button
          onClick={() => onDeactivate(cls, student)}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-red-200 text-red-600 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Deactivate — mark expired and unenroll"
        >
          {isLoading
            ? <RefreshCw size={13} className="animate-spin" />
            : <UserX size={13} />
          }
          Deactivate
        </button>
      </div>
    </div>
  );
}

// ─── Row for the "Not Enrolled" tab ────────────────────────────────────────────
// Unenrolled students have no class and no session count, so only Pause and
// Deactivate apply. Expired students show no actions (nothing to do).

function UnenrolledStudentRow({ entry, onDeactivate, onPause, actionLoading }) {
  const { student, status, childName, parentFirstName, parentLastName, parentEmail } = entry;
  const isLoading = actionLoading === entry.key;
  const canAct = status === 'active' || status === 'completed' || status === 'pending';

  const statusPill = {
    active:    'bg-green-100 text-green-700',
    completed: 'bg-blue-100 text-blue-700',
    pending:   'bg-amber-100 text-amber-700',
    expired:   'bg-gray-100 text-gray-600',
  }[status] || 'bg-gray-100 text-gray-500';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-800">{childName}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusPill}`}>
            {status}
          </span>
          {entry.isLegacy && (
            <span className="text-[10px] text-gray-400 italic">(legacy account)</span>
          )}
        </div>
        <div className="text-sm text-gray-500 mt-0.5">
          {[parentFirstName, parentLastName].filter(Boolean).join(' ')}
          {parentEmail ? ` · ${parentEmail}` : ''}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          Not assigned to a class
        </div>
      </div>

      <div className="flex items-center gap-2">
        {canAct ? (
          <>
            <button
              onClick={() => onPause(null, student)}
              disabled={isLoading}
              title="Pause — track for return"
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-amber-200 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? <RefreshCw size={13} className="animate-spin" />
                : <Pause size={13} />}
              Pause
            </button>
            <button
              onClick={() => onDeactivate(null, student)}
              disabled={isLoading}
              title="Deactivate — mark expired"
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-red-200 text-red-600 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? <RefreshCw size={13} className="animate-spin" />
                : <UserX size={13} />}
              Deactivate
            </button>
          </>
        ) : (
          <span className="text-xs text-gray-400 italic">No action needed</span>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

function RenewalManager() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [classes,  setClasses]  = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [activeFilter, setActiveFilter] = useState(0);    // sessions remaining
  const [actionLoading, setActionLoading] = useState(null); // "classId__childId"
  const [searchTerm, setSearchTerm] = useState('');

  // Pause modal state
  const [pauseModal, setPauseModal] = useState({
    open: false,
    cls: null,
    student: null,
    expectedReturnDate: '',
    notes: '',
    submitting: false,
  });

  const [activeView, setActiveView] = useState('enrolled'); // 'enrolled' | 'unenrolled'
  const [unenrolledStatusFilter, setUnenrolledStatusFilter] = useState('active'); // default to Active

  // ── Flash helper ──────────────────────────────────────────────────────────────
  function showSuccess(msg) {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  // ── Data loading ──────────────────────────────────────────────────────────────

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);

      const [classSnap, teacherSnap, userSnap] = await Promise.all([
        getDocs(query(collection(db, 'classes'), orderBy('dayOfWeek'))),
        getDocs(query(collection(db, 'teachers'), orderBy('name'))),
        getDocs(collection(db, 'users')),
      ]);

      setClasses(classSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTeachers(teacherSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAllUsers(userSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
    } catch (err) {
      console.error('RenewalManager fetch error:', err);
      setError('Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Build flat enrollment list ────────────────────────────────────────────────
  // Each entry: { student, cls, teacher, userCourseStatus, remaining }

  const allEntries = useMemo(() => {
    const entries = [];

    classes.forEach(cls => {
      if (cls.status === 'suspended') return; // skip suspended classes
      const teacher = teachers.find(t => t.id === cls.teacherId);

      (cls.enrolledStudents ?? []).forEach(student => {
        // Look up live course status from users collection
        const userData = allUsers.find(u => u.uid === student.userId);
        let userCourseStatus = null;
        if (userData?.children) {
          const child = userData.children.find(c => c.id === student.childId);
          userCourseStatus = child?.course?.status ?? null;
        } else if (userData?.course) {
          // Legacy single-child structure
          userCourseStatus = userData.course.status ?? null;
        }

        entries.push({
          student,
          cls,
          teacher,
          userCourseStatus,
          remaining: sessionsRemaining(student),
          // Composite key for action loading state
          key: `${cls.id}__${student.childId}`,
        });
      });
    });

    // Sort: 0 remaining first, then 1, 2, 3...
    entries.sort((a, b) => a.remaining - b.remaining);
    return entries;
  }, [classes, teachers, allUsers]);

  // ── Filter + search ───────────────────────────────────────────────────────────

  const filteredEntries = useMemo(() => {
    let result = activeFilter === -1
      ? allEntries
      : allEntries.filter(e => e.remaining === activeFilter);

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(e =>
        e.student.childName?.toLowerCase().includes(q) ||
        e.student.parentFirstName?.toLowerCase().includes(q) ||
        e.student.parentLastName?.toLowerCase().includes(q) ||
        e.student.parentEmail?.toLowerCase().includes(q) ||
        e.teacher?.name?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [allEntries, activeFilter, searchTerm]);

  // ── Tab counts ────────────────────────────────────────────────────────────────

  const tabCounts = useMemo(() => {
    const counts = {};
    FILTER_TABS.forEach(tab => {
      counts[tab.value] = tab.value === -1
        ? allEntries.length
        : allEntries.filter(e => e.remaining === tab.value).length;
    });
    return counts;
  }, [allEntries]);

  // ── Build "not enrolled" list ───────────────────────────────────────────────
  // Every COURSED child (across all users) NOT present in any active class's
  // enrolledStudents. Used by the "Not Enrolled" tab.

  const unenrolledEntries = useMemo(() => {
    // Build a set of "userId__childId" keys for every enrolled student
    const enrolledKeys = new Set();
    classes.forEach(cls => {
      if (cls.status === 'suspended') return;
      (cls.enrolledStudents ?? []).forEach(s => {
        enrolledKeys.add(`${s.userId}__${s.childId}`);
      });
    });

    const out = [];
    allUsers.forEach(u => {
      // New multi-child structure
      if (Array.isArray(u.children)) {
        u.children.forEach(child => {
          // Stage 5 audit: a trial student is a LEAD, not a customer. A
          // courseless child has nothing to renew — this tab is renewal
          // management for paying customers, so leads never belong on it.
          if (!child.course) return;
          const key = `${u.uid}__${child.id}`;
          if (enrolledKeys.has(key)) return; // skip enrolled
          out.push({
            userId: u.uid,
            childId: child.id,
            childName: child.name || 'Unknown',
            parentFirstName: u.firstName || u.parentName || '',
            parentLastName: u.lastName || '',
            parentEmail: u.email || '',
            status: child.course?.status || 'unknown',
            packageId: child.course?.packageId || null,
            courseEndDate: child.course?.endDate || null,
            // entry shape for the shared Pause modal / Deactivate handler:
            // cls is null because this child is not in a class
            student: {
              userId: u.uid,
              childId: child.id,
              childName: child.name || 'Unknown',
              parentFirstName: u.firstName || u.parentName || '',
              parentLastName: u.lastName || '',
              parentEmail: u.email || '',
            },
            key: `unenrolled__${u.uid}__${child.id}`,
          });
        });
      }
      // Legacy single-child structure (no children array)
      else if (u.course) {
        // Legacy users have no per-child id; represent with a synthetic id.
        const childId = u.childId || 'legacy';
        const key = `${u.uid}__${childId}`;
        if (enrolledKeys.has(key)) return;
        out.push({
          userId: u.uid,
          childId,
          childName: u.childName || u.parentName || 'Unknown (legacy)',
          parentFirstName: u.firstName || u.parentName || '',
          parentLastName: u.lastName || '',
          parentEmail: u.email || '',
          status: u.course?.status || 'unknown',
          packageId: u.course?.packageId || null,
          courseEndDate: u.course?.endDate || null,
          student: {
            userId: u.uid,
            childId,
            childName: u.childName || u.parentName || 'Unknown (legacy)',
            parentFirstName: u.firstName || u.parentName || '',
            parentLastName: u.lastName || '',
            parentEmail: u.email || '',
          },
          key: `unenrolled__${u.uid}__${childId}`,
          isLegacy: true,
        });
      }
    });

    return out;
  }, [allUsers, classes]);

  // Counts per status, for the filter chips
  const unenrolledStatusCounts = useMemo(() => {
    const counts = { all: unenrolledEntries.length };
    UNENROLLED_STATUS_FILTERS.forEach(f => {
      if (f.value === 'all') return;
      counts[f.value] = unenrolledEntries.filter(e => e.status === f.value).length;
    });
    return counts;
  }, [unenrolledEntries]);

  // Apply status filter + search to the unenrolled list
  const filteredUnenrolled = useMemo(() => {
    let result = unenrolledStatusFilter === 'all'
      ? unenrolledEntries
      : unenrolledEntries.filter(e => e.status === unenrolledStatusFilter);

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(e =>
        e.childName?.toLowerCase().includes(q) ||
        e.parentFirstName?.toLowerCase().includes(q) ||
        e.parentLastName?.toLowerCase().includes(q) ||
        e.parentEmail?.toLowerCase().includes(q)
      );
    }

    // Sort by child name for predictable scanning
    return [...result].sort((a, b) =>
      (a.childName || '').toLowerCase().localeCompare((b.childName || '').toLowerCase())
    );
  }, [unenrolledEntries, unenrolledStatusFilter, searchTerm]);

  // ── Add 6 sessions (manual renewal) ──────────────────────────────────────────

  const handleRenew = async (cls, student, amount) => {
    const completed = student.sessionsCompleted ?? 0;
    const currentTotal = student.sessionsTotal ?? 6;
    const newTotal = currentTotal + amount;

    // Guard: covers both newTotal < 0 and reducing below already-completed sessions
    if (newTotal < completed) {
      setError(
        `Can't set total to ${newTotal} — ${student.childName} has already completed ${completed} session${completed !== 1 ? 's' : ''}.`
      );
      return;
    }

    const absAmount = Math.abs(amount);
    const direction = amount >= 0 ? 'Add' : 'Remove';

    if (!window.confirm(
      `${direction} ${absAmount} session${absAmount !== 1 ? 's' : ''} for ${student.childName}?\n` +
      `Sessions total: ${currentTotal} → ${newTotal}`
    )) return;

    const key = `${cls.id}__${student.childId}`;
    setActionLoading(key);

    try {
      // 1. Update sessionsTotal in the class document
      const updatedStudents = (cls.enrolledStudents ?? []).map(s => {
        if (s.userId === student.userId && s.childId === student.childId) {
          return { ...s, sessionsTotal: newTotal };
        }
        return s;
      });

      await updateDoc(doc(db, 'classes', cls.id), {
        enrolledStudents: updatedStudents,
      });

      // 2. Update course status in users document if needed
      // Always read fresh from Firestore — allUsers state may be stale
      // (the cloud function may have updated course.status after this page loaded)
      const freshUserDoc = await getDoc(doc(db, 'users', student.userId));
      const userData = freshUserDoc.exists ? { uid: student.userId, ...freshUserDoc.data() } : null;
      if (userData?.children) {
        const childIndex = userData.children.findIndex(c => c.id === student.childId);
        if (childIndex !== -1) {
          const child = userData.children[childIndex];
          const currentStatus = child.course?.status;

      let newStatus = currentStatus;
          if (amount > 0 && newTotal > completed) {
            newStatus = 'active'; // always reactivate when sessions create remaining headroom
          } else if (amount < 0 && completed >= newTotal) {
            newStatus = 'completed'; // mark complete if now at/over the new limit
          }

          // Always write when adding sessions — don't skip based on currentStatus
          // since it may be stale or set by a different code path
          if (amount > 0 && newTotal > completed) {
            const updatedChildren = [...userData.children];
            updatedChildren[childIndex] = {
              ...child,
              course: {
                ...child.course,
                status: newStatus,
                updatedAt: new Date().toISOString(),
              },
            };
            await updateDoc(doc(db, 'users', student.userId), {
              children: updatedChildren,
            });
          } else if (newStatus !== currentStatus) {
            const updatedChildren = [...userData.children];
            updatedChildren[childIndex] = {
              ...child,
              course: {
                ...child.course,
                status: newStatus,
                updatedAt: new Date().toISOString(),
              },
            };
            await updateDoc(doc(db, 'users', student.userId), {
              children: updatedChildren,
            });
          }
        }
      }

      const verb = amount >= 0 ? 'added' : 'removed';
      showSuccess(`${absAmount} session${absAmount !== 1 ? 's' : ''} ${verb} for ${student.childName}.`);
      await fetchAll();
    } catch (err) {
      console.error('Renew error:', err);
      setError(`Failed to adjust sessions for ${student.childName}.`);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Pause (track for return) ─────────────────────────────────────────────────
  // Same destructive action as Deactivate (status='expired', unenroll from class)
  // PLUS appends a followUps entry capturing expected return date + notes.

  const openPauseModal = (cls, student) => {
    setPauseModal({
      open: true,
      cls,
      student,
      expectedReturnDate: '',
      notes: '',
      submitting: false,
    });
  };

  const closePauseModal = () => {
    setPauseModal({
      open: false,
      cls: null,
      student: null,
      expectedReturnDate: '',
      notes: '',
      submitting: false,
    });
  };

  const handlePauseConfirm = async () => {
    const { cls, student, expectedReturnDate, notes } = pauseModal;
    if (!student) return;

    if (!expectedReturnDate) {
      setError('Expected return date is required.');
      return;
    }

    setPauseModal(prev => ({ ...prev, submitting: true }));
    const key = cls ? `${cls.id}__${student.childId}` : `unenrolled__${student.userId}__${student.childId}`;
    setActionLoading(key);

    try {
      const now = new Date().toISOString();
      const userData = allUsers.find(u => u.uid === student.userId);

      const newFollowUp = {
        id: `fu_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        expectedReturnDate, // 'YYYY-MM-DD'
        notes: notes?.trim() || '',
        createdAt: now,
        createdBy: user?.uid || 'unknown_admin',
        contactedAt: null,
        resolvedAt: null,
        resolvedReason: null,
        resolvedBy: null,
      };

      // 1. Mark course expired AND append followUps entry in users collection
      if (userData) {
        if (userData.children) {
          const childIndex = userData.children.findIndex(c => c.id === student.childId);
          if (childIndex !== -1) {
            const updatedChildren = userData.children.map((child, idx) => {
              if (idx !== childIndex) return child;
              const existingFollowUps = Array.isArray(child.followUps) ? child.followUps : [];
              return {
                ...child,
                course: {
                  ...child.course,
                  status: 'expired',
                  updatedAt: now,
                },
                followUps: [...existingFollowUps, newFollowUp],
                statusHistory: appendStatusHistoryFE(child, 'expired', 'pause'),
              };
            });
            await updateDoc(doc(db, 'users', student.userId), {
              children: updatedChildren,
            });
          }
        } else if (userData.course) {
          // Legacy single-child structure — no followUps support, just deactivate
          await updateDoc(doc(db, 'users', student.userId), {
            'course.status': 'expired',
            'course.updatedAt': now,
          });
          console.warn('Pause: legacy structure detected — followUp NOT written, deactivation only.');
        }
      }

      // 2. Remove from class enrolledStudents (skipped for unenrolled students)
      if (cls) {
        const freshClassSnap = await getDocs(
          query(collection(db, 'classes'), orderBy('dayOfWeek'))
        );
        const freshClass = freshClassSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .find(c => c.id === cls.id);

        if (freshClass) {
          const studentToRemove = (freshClass.enrolledStudents ?? []).find(
            s => s.userId === student.userId && s.childId === student.childId
          );
          if (studentToRemove) {
            await updateDoc(doc(db, 'classes', cls.id), {
              enrolledStudents: arrayRemove(studentToRemove),
            });
          }
        }
      }

      showSuccess(`${student.childName} paused. Follow-up entry created.`);
      closePauseModal();
      await fetchAll();
    } catch (err) {
      console.error('Pause error:', err);
      setError(`Failed to pause ${student.childName}.`);
    } finally {
      setActionLoading(null);
      setPauseModal(prev => ({ ...prev, submitting: false }));
    }
  };

  // ── Deactivate ────────────────────────────────────────────────────────────────
  // 1. Sets course.status = 'expired' on the child in users collection
  // 2. Removes student from enrolledStudents in the class document
  // Session history is never deleted.

  const handleDeactivate = async (cls, student) => {
    const classLine = cls
      ? `• Remove them from the ${dayLabel(cls.dayOfWeek)} ${formatTime(cls.time)} class\n`
      : '';
    if (!window.confirm(
      `Deactivate ${student.childName}?\n\n` +
      `This will:\n` +
      `• Mark their course as expired\n` +
      classLine +
      `• Preserve all session history\n\n` +
      `This can be reversed by re-enrolling them in ClassManager.`
    )) return;

    const key = cls ? `${cls.id}__${student.childId}` : `unenrolled__${student.userId}__${student.childId}`;
    setActionLoading(key);

    try {
      // 1. Mark course expired in users collection
      const userData = allUsers.find(u => u.uid === student.userId);
      if (userData) {
        if (userData.children) {
          // New multi-child structure
          const childIndex = userData.children.findIndex(c => c.id === student.childId);
          if (childIndex !== -1) {
            const updatedChildren = [...userData.children];
            updatedChildren[childIndex] = {
              ...updatedChildren[childIndex],
              course: {
                ...updatedChildren[childIndex].course,
                status: 'expired',
                updatedAt: new Date().toISOString(),
              },
              statusHistory: appendStatusHistoryFE(updatedChildren[childIndex], 'expired', 'deactivate'),
            };
            await updateDoc(doc(db, 'users', student.userId), {
              children: updatedChildren,
            });
          }
        } else if (userData.course) {
          // Legacy single-child structure
          await updateDoc(doc(db, 'users', student.userId), {
            'course.status': 'expired',
            'course.updatedAt': new Date().toISOString(),
          });
        }
      }

      // 2. Remove from class enrolledStudents (skipped for unenrolled students)
      if (cls) {
        const freshClassSnap = await getDocs(
          query(collection(db, 'classes'), orderBy('dayOfWeek'))
        );
        const freshClass = freshClassSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .find(c => c.id === cls.id);

        if (freshClass) {
          const studentToRemove = (freshClass.enrolledStudents ?? []).find(
            s => s.userId === student.userId && s.childId === student.childId
          );
          if (studentToRemove) {
            await updateDoc(doc(db, 'classes', cls.id), {
              enrolledStudents: arrayRemove(studentToRemove),
            });
          }
        }
      }

      showSuccess(cls
        ? `${student.childName} deactivated and removed from class.`
        : `${student.childName} deactivated.`);
      await fetchAll();
    } catch (err) {
      console.error('Deactivate error:', err);
      setError(`Failed to deactivate ${student.childName}.`);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading renewals...</p>
        </div>
      </div>
    );
  }

  const activeTab = FILTER_TABS.find(t => t.value === activeFilter);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Renewals</h1>
              <p className="text-sm text-gray-500">
                {tabCounts[0]} completed · {tabCounts[1] ?? 0} with 1 session left
              </p>
            </div>
          </div>
          <button
            onClick={fetchAll}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>

        {/* View switcher: Enrolled vs Not Enrolled */}
        <div className="flex bg-gray-200 p-1 rounded-xl gap-1 w-fit">
        <button
            onClick={() => setActiveView('enrolled')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors relative ${
              activeView === 'enrolled'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Enrolled
            {allEntries.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-4 px-1 text-xs rounded-full bg-gray-400 text-white">
                {allEntries.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveView('unenrolled')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors relative ${
              activeView === 'unenrolled'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Not Enrolled
            {unenrolledEntries.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-4 px-1 text-xs rounded-full bg-gray-400 text-white">
                {unenrolledEntries.length}
              </span>
            )}
          </button>
        </div>

        {/* Flash messages */}
        {successMessage && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
            <CheckCircle size={16} /> {successMessage}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} /> {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={14} />
            </button>
          </div>
        )}

        {activeView === 'enrolled' && (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {FILTER_TABS.filter(t => t.value !== -1).map(tab => (
                <button
                  key={tab.value}
                  onClick={() => setActiveFilter(tab.value)}
                  className={`p-4 rounded-xl border text-left transition-colors ${
                    activeFilter === tab.value
                      ? tab.urgent
                        ? 'bg-red-50 border-red-300'
                        : 'bg-purple-50 border-purple-300'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <p className={`text-2xl font-bold ${
                    tab.urgent && (tabCounts[tab.value] ?? 0) > 0
                      ? 'text-red-600'
                      : 'text-gray-800'
                  }`}>
                    {tabCounts[tab.value] ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{tab.description}</p>
                </button>
              ))}
            </div>

            {/* Filter tabs + search */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Tab pills */}
              <div className="flex bg-gray-200 p-1 rounded-xl gap-1">
                {FILTER_TABS.map(tab => (
                  <button
                    key={tab.value}
                    onClick={() => setActiveFilter(tab.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative ${
                      activeFilter === tab.value
                        ? 'bg-white text-gray-800 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                    {tab.value !== -1 && (tabCounts[tab.value] ?? 0) > 0 && (
                      <span className={`ml-1.5 inline-flex items-center justify-center w-4 h-4 text-xs rounded-full ${
                        tab.urgent && tab.value === 0
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-400 text-white'
                      }`}>
                        {tabCounts[tab.value]}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Search */}
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name or email…"
                className="flex-1 min-w-[200px] border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Context description */}
            {activeTab && (
              <p className="text-sm text-gray-500 -mt-2">
                <Filter size={13} className="inline mr-1" />
                {activeTab.description}
                {filteredEntries.length > 0 && ` — ${filteredEntries.length} student${filteredEntries.length !== 1 ? 's' : ''}`}
              </p>
            )}

            {/* Student list */}
            {filteredEntries.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Users size={40} className="text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">
                  {searchTerm
                    ? 'No students match your search'
                    : activeFilter === 0
                    ? 'No completed courses — great!'
                    : `No students with ${activeFilter} session${activeFilter !== 1 ? 's' : ''} remaining`
                  }
                </p>
                {activeFilter === 0 && !searchTerm && (
                  <p className="text-sm text-gray-400 mt-1">
                    Students will appear here when they reach their session limit.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEntries.map(entry => (
                  <StudentRow
                    key={entry.key}
                    entry={entry}
                    onRenew={handleRenew}
                    onDeactivate={handleDeactivate}
                    onPause={openPauseModal}
                    actionLoading={actionLoading}
                    db={db}
                  />
                ))}
              </div>
            )}

            {/* Footer note */}
            <p className="text-xs text-gray-400 text-center pb-4">
              +6 Sessions manually adds to the session count and reactivates the course — change the number to add or remove any amount.
              Stripe renewal automation will replace this in a future phase.
              Deactivating a student preserves all session history.
            </p>
          </>
        )}

        {activeView === 'unenrolled' && (
          <>
            {/* Status filter chips */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex bg-gray-200 p-1 rounded-xl gap-1 flex-wrap">
                {UNENROLLED_STATUS_FILTERS.map(f => (
                  <button
                    key={f.value}
                    onClick={() => setUnenrolledStatusFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      unenrolledStatusFilter === f.value
                        ? 'bg-white text-gray-800 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {f.label}
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-xs rounded-full bg-gray-400 text-white">
                      {unenrolledStatusCounts[f.value] ?? 0}
                    </span>
                  </button>
                ))}
              </div>

              {/* Search (shares the same searchTerm state as the enrolled view) */}
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name or email…"
                className="flex-1 min-w-[200px] border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <p className="text-sm text-gray-500">
              <Filter size={13} className="inline mr-1" />
              Students not assigned to any class
              {filteredUnenrolled.length > 0 && ` — ${filteredUnenrolled.length} student${filteredUnenrolled.length !== 1 ? 's' : ''}`}
            </p>

            {filteredUnenrolled.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Users size={40} className="text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">
                  {searchTerm
                    ? 'No students match your search'
                    : 'No unenrolled students with this status'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredUnenrolled.map(entry => (
                  <UnenrolledStudentRow
                    key={entry.key}
                    entry={entry}
                    onDeactivate={handleDeactivate}
                    onPause={openPauseModal}
                    actionLoading={actionLoading}
                  />
                ))}
              </div>
            )}
          </>
        )}

      </div>

      {/* Pause Modal */}
      {pauseModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Pause size={18} className="text-amber-600" />
                Pause {pauseModal.student?.childName}
              </h3>
              <button
                onClick={closePauseModal}
                disabled={pauseModal.submitting}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              This will mark the course as expired and unenroll {pauseModal.student?.childName} from the class — same as Deactivate. Additionally, a follow-up entry will be created so you can track their return.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected return date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={pauseModal.expectedReturnDate}
                  onChange={(e) => setPauseModal(prev => ({ ...prev, expectedReturnDate: e.target.value }))}
                  disabled={pauseModal.submitting}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={pauseModal.notes}
                  onChange={(e) => setPauseModal(prev => ({ ...prev, notes: e.target.value }))}
                  disabled={pauseModal.submitting}
                  rows={3}
                  placeholder="e.g., Traveling for the summer, returning end of August"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={closePauseModal}
                disabled={pauseModal.submitting}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePauseConfirm}
                disabled={pauseModal.submitting || !pauseModal.expectedReturnDate}
                className="flex-1 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {pauseModal.submitting ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    Pausing...
                  </>
                ) : (
                  <>
                    <Pause size={14} />
                    Confirm Pause
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RenewalManager;